import { useState, type FormEvent } from 'react'
import { Button } from '@/components/ui/Button'
import { Modal } from '@/components/ui/Modal'
import { RoleSelect } from '@/components/ui/RoleSelect'
import { TextField } from '@/components/ui/TextField'
import type { UserRecord } from '@/data/useUsers'
import { resetUserPassword, setUserRole } from '@/data/userApi'
import { errorMessage } from '@/lib/errors'
import type { Role } from '@/types/user'

interface EditUserModalProps {
  user: UserRecord | null
  onClose: () => void
  onSaved: () => void
}

// Rendered with a `key` of the user id (see UsersPage), so state initialises
// fresh from props each time a different user is edited — no syncing effect.
export function EditUserModal({ user, onClose, onSaved }: EditUserModalProps) {
  const [role, setRole] = useState<Role>(user?.role ?? 'supplier')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!user) {
      return
    }
    if (password && password.length < 6) {
      setError('Password must be at least 6 characters.')
      return
    }
    setError(null)
    setSubmitting(true)
    try {
      if (role !== user.role) {
        await setUserRole(user.id, role)
      }
      if (password) {
        await resetUserPassword(user.id, password)
      }
      onSaved()
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal open={user !== null} title="Edit user" onClose={onClose}>
      <form className="space-y-4" onSubmit={handleSubmit} noValidate>
        <div className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-600">
          <div className="font-medium text-slate-800">{user?.name}</div>
          <div>{user?.email}</div>
        </div>
        <RoleSelect id="edit-user-role" value={role} onChange={setRole} />
        <TextField
          id="edit-user-password"
          label="Reset password (optional)"
          type="password"
          value={password}
          placeholder="Leave blank to keep current password"
          onChange={(event) => setPassword(event.target.value)}
          autoComplete="new-password"
        />
        {error && (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={submitting}>
            {submitting ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </form>
    </Modal>
  )
}
